import React from "react";
import { Flag, CheckCircle, Users, Map, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface StatsSectionProps {
  animatedStats: {
    signalements: number;
    resolutions: number;
    utilisateurs: number;
    communes: number;
  };
}

const StatsSection = ({ animatedStats }: StatsSectionProps) => {
  const navigate = useNavigate();

  const stats = [
    { label: "Signalements", value: animatedStats.signalements.toLocaleString(), desc: "ce mois-ci", icon: Flag },
    { label: "Résolutions",  value: animatedStats.resolutions.toLocaleString(),  desc: "problèmes résolus", icon: CheckCircle },
    { label: "Utilisateurs", value: animatedStats.utilisateurs.toLocaleString(), desc: "citoyens actifs", icon: Users },
    { label: "Communes",     value: animatedStats.communes.toLocaleString(),     desc: "partenaires", icon: Map },
  ];

  return (
    <div>
      {/* Header */}
      <div className="max-w-2xl mb-14 md:mb-20">
        <span className="ed-eyebrow mb-5 block">02 — Impact collectif</span>
        <h2 className="ed-heading text-3xl md:text-5xl mb-5">
          Des chiffres qui <span className="accent">racontent</span> notre engagement
        </h2>
        <p className="ed-subtle text-base md:text-lg leading-relaxed max-w-lg">
          Chaque signalement est une action concrète. Ensemble, les citoyens et les communes construisent des villes plus vivables.
        </p>
      </div>

      <div className="ed-rule mb-10" />

      {/* Stats grid — editorial clean */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
        {stats.map((stat, i) => (
          <div
            key={i}
            className="group ed-fade-up"
            style={{ animationDelay: `${i * 120}ms` }}
          >
            {/* Icon + index */}
            <div className="flex items-center justify-between mb-5">
              <div className="w-10 h-10 rounded-full bg-[var(--ed-navy-800)]/5 flex items-center justify-center group-hover:bg-[var(--ed-navy-800)]/10 transition-colors duration-500">
                <stat.icon className="h-4 w-4 text-[var(--ed-navy-500)]" strokeWidth={1.5} />
              </div>
              <span className="font-display text-[10px] uppercase tracking-[0.25em] text-[var(--ed-navy-500)]">
                {String(i + 1).padStart(2, '0')}
              </span>
            </div>

            {/* Value */}
            <div className="ed-numeric text-4xl md:text-5xl text-[var(--ed-navy-800)] mb-2 tracking-tight">
              {stat.value}
            </div>

            {/* Label */}
            <div className="font-display text-sm font-semibold uppercase tracking-wider text-[var(--ed-ink)] mb-1">
              {stat.label}
            </div>

            {/* Description */}
            <div className="text-xs text-[rgba(10,15,31,0.55)] font-light leading-relaxed">
              {stat.desc}
            </div>
          </div>
        ))}
      </div>

      {/* Editorial CTA */}
      <div className="mt-14 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        <div className="h-px w-16 bg-[var(--ed-navy-500)] hidden sm:block" />
        <button
          onClick={() => navigate('/dashboard')}
          className="ed-btn-ghost inline-flex items-center gap-2 text-sm md:text-base"
        >
          Statistiques détaillées <ArrowUpRight className="h-4 w-4" />
        </button>
        <span className="text-xs text-[rgba(10,15,31,0.40)] font-body tracking-wide hidden sm:inline">
          Tableau de bord public · Mises à jour temps réel
        </span>
      </div>
    </div>
  );
};

export default StatsSection;
